import request from '@/utils/request'
import { v2Host } from '@/config/host'
import { AxiosRequestConfig } from 'axios'
import type { _Response } from '../type'
import {
  AdAccount,
  AdAccountLite,
  AdSubAccount,
  GetAdAccountListQuery,
  GetAdAccountSearchQuery,
  CampaignSet,
  AdAccountAuthQuery,
  AdAccountBindQuery,
  AdAuthInfo,
  AdReportPermissionQuery,
  ProfileInfo,
  GetBillingUsageQuery,
  BillingUsage,
  GetBillDetailQuery,
  BillDetail,
  GetBillsQuery,
  Bills,
  GetBillingMaterialsQuery,
  BillingMaterial,
  AdAccountChannel,
  StatementQuery,
  StatementItem,
  PointSetting,
  CreativeSet,
  UserFolder,
  ApplyTrailQuery,
  ExistsPermission,
  UserPartnerResponse,
  UserAssistantsInfo,
  ApplyTrailResponse,
} from './type'
import type { ListData } from 'request'
import { TrafficType } from '@pt/enums/insights'
import { resolveData } from '../insights'
import { cookRequestParams } from '@/common/request'

export function modifyPassword(data) {
  return request({
    url: `${v2Host}/user/change-password`,
    method: 'post',
    data: data,
  })
}

export function getAdvUserInfo(params) {
  return request({
    url: `${v2Host}/user/me`,
    method: 'get',
    params: params,
  })
}

export function agreePrivacyAgreement() {
  return request({
    url: `${v2Host}/user/agree-privacy-agreement`,
    method: 'post',
  })
}

// 授权广告帐号列表
export function getAdAccountSearch(params: GetAdAccountSearchQuery): _Response<{
  list: Partial<Record<TrafficType, AdAccountLite[]>>
  xmp: AdAccountLite[]
}> {
  return request({
    url: `${v2Host}/user/ad-account/search`,
    method: 'get',
    params: {
      ...params,
      traffic_types: params.traffic_types?.join(','),
    },
  })
}

// 授权广告帐号列表
export function getAdAccountList(
  params: GetAdAccountListQuery,
): _Response<ListData<AdAccount>> {
  return request({
    url: `${v2Host}/user/ad-account/list`,
    method: 'get',
    params: {
      ...params,
      statuses: params.statuses?.join(','),
    },
  })
}

export function getAdSubAccountList(params: {
  id: number
}): _Response<ListData<AdSubAccount>> {
  return request({
    url: `${v2Host}/user/ad-account/sub-list`,
    method: 'get',
    params,
  })
}

// Unity CampaignSet 列表
export function getCampaignSets(): _Response<{
  list: Record<number, CampaignSet[]>
}> {
  return request({
    url: `${v2Host}/user/ad-account/apps`,
    method: 'get',
  })
}

export function getCreativeSets(): _Response<{
  list: Record<number, CreativeSet[]>
}> {
  return request({
    url: `${v2Host}/user/ad-account/creative-sets`,
    method: 'get',
  })
}

// 提交广告帐号授权
export function submitAdAccountAuth(
  data: AdAccountAuthQuery,
): _Response<unknown> {
  return request({
    url: `${v2Host}/user/ad-account/auth`,
    method: 'post',
    data: {
      ...data,
    },
  })
}

export function updateAdReportPermission(
  data: AdReportPermissionQuery,
): _Response<unknown> {
  return request({
    url: `${v2Host}/user/ad-account/update-report-permission`,
    method: 'post',
    data,
  })
}

export function getAdAuthInfo(): _Response<{
  info: AdAuthInfo
}> {
  return request({
    url: `${v2Host}/user/ad-account/auth-info`,
    method: 'get',
  })
}

// 绑定
export function adAccountBind(data: AdAccountBindQuery): _Response<unknown> {
  return request({
    url: `${v2Host}/user/ad-account/bind`,
    method: 'post',
    data,
  })
}

// 解绑
export function adAccountUnbind(data: { id: number }): _Response<unknown> {
  return request({
    url: `${v2Host}/user/ad-account/unbind`,
    method: 'post',
    data,
  })
}

// 删除授权记录
export function adAccountDelete(data: { id: number }): _Response<unknown> {
  return request({
    url: `${v2Host}/user/ad-account/delete`,
    method: 'post',
    data,
  })
}

// 个人信息
export function getProfile(): _Response<ProfileInfo> {
  return request({
    url: `${v2Host}/user/profile`,
    method: 'get',
  })
}

// 个人信息
export function updateProfile(data: {
  cc_email: string[]
  cell: string
  contact: string
  organization: string
  country: string
}): _Response<unknown> {
  console.log(data)
  return request({
    url: `${v2Host}/user/update-profile`,
    method: 'post',
    data,
  })
}

/**
 * 套餐使用情况
 */
export function getBillingUsage(
  params: GetBillingUsageQuery,
): _Response<ListData<BillingUsage>> {
  return request({
    url: `${v2Host}/user/billing/usage`,
    method: 'get',
    params,
  })
}

/**
 * 账单明细
 */
export function getBillingDetail(
  params: GetBillDetailQuery,
): _Response<ListData<BillDetail>> {
  return request({
    url: `${v2Host}/user/billing/bill-detail`,
    method: 'get',
    params,
  })
}

/**
 * 月度账单
 */
export function getBillingBills(
  params: GetBillsQuery,
): _Response<ListData<Bills>> {
  return request({
    url: `${v2Host}/user/billing/bills`,
    method: 'get',
    params,
  })
}

/**
 * 素材费用消耗
 */
export function getBillingMaterials(
  params: GetBillingMaterialsQuery,
): _Response<ListData<BillingMaterial>> {
  return request({
    url: `${v2Host}/user/billing/materials`,
    method: 'get',
    params,
  })
}

// 渠道授权信息或下载记录
export function adAccountChannelDataDetail(params: {
  channel_ids: TrafficType[]
}): _Response<{
  channels: null | Array<AdAccountChannel>
}> {
  return request({
    url: `${v2Host}/user/ad-account/channels`,
    method: 'get',
    params: resolveData(params),
  })
}

/**
 * 账单流水
 */
export function getBillingBillFlow(
  params: StatementQuery,
): _Response<ListData<StatementItem>> {
  return request({
    url: `${v2Host}/user/billing/bill-flow`,
    method: 'get',
    params: {
      ...params,
      traffic_types: params.traffic_types?.join(','),
      basic_ids: params.basic_ids?.join(','),
    },
  })
}

export interface BillOptionsResponseData {
  list: Array<{
    value: string
    name: string
  }> | null
}

export function getBillOptions(params: {
  keyword: string
  page_size: number
  search_field: string
}): _Response<BillOptionsResponseData> {
  return request({
    url: `${v2Host}/user/billing/options`,
    method: 'get',
    params,
  })
}

/**
 * 获取用户渠道埋点配置
 */
export function getUserReportPoint(): _Response<PointSetting[]> {
  return request({
    url: `${v2Host}/user/report-point-setting`,
    method: 'get',
  })
}

export function updateUserReportPoint(
  data: PointSetting[],
): _Response<unknown> {
  return request({
    url: `${v2Host}/user/report-point-setting`,
    method: 'post',
    data,
  })
}

// 计算业务消耗点数
export function getPredictCost(
  data: {
    biz_type: string
    durations?: number[]
    num?: number
  },
  options: AxiosRequestConfig = {},
): _Response<{
  amount: number
  balance: number
  costs: number[]
  usage_type: string
}> {
  return request({
    url: `${v2Host}/user/balance/predict-cost`,
    method: 'post',
    data,
    ...options,
  })
}

// 申请试用
export function applyTrail(
  data: ApplyTrailQuery,
  options: AxiosRequestConfig = {},
): _Response<ApplyTrailResponse> {
  return request({
    url: `${v2Host}/user/apply-trial`,
    method: 'post',
    data: cookRequestParams(data),
    ...options,
  })
}

// 用户留言
export function leaveMessage(
  data: {
    content: string
  },
  options: AxiosRequestConfig = {},
): _Response<unknown> {
  return request({
    url: `${v2Host}/user/leave-msg`,
    method: 'post',
    data,
    ...options,
  })
}
/**
 * 获取合作方信息
 */
export function getUserPartnerInfo(): _Response<UserPartnerResponse> {
  return request({
    url: `${v2Host}/user/partner-info`,
    method: 'get',
  })
}

// 校验用户注册信息
export function checkEmail(
  email: string,
): _Response<{ exists: ExistsPermission } | null> {
  return request({
    url: `${v2Host}/user/check`,
    method: 'get',
    params: {
      email,
    },
  })
}

/**
 * 获取xmp用户对应的推送文件夹
 */
export function getUserFolders(): _Response<{
  data: Record<number, UserFolder[]>
}> {
  return request({
    url: `${v2Host}/user/ad-account/folders`,
    method: 'get',
  })
}

/**
 * 用户同意协议勾选需要调用协议记录接口
 */
export function updateUserProtocol(data: {
  biz_type: string
}): _Response<unknown> {
  return request({
    url: `${v2Host}/user/agreement`,
    method: 'post',
    data,
  })
}

/**
 * 添加协助人
 */
export function addAssistant(data: {
  id: number
  assistant_name: string
}): _Response<unknown> {
  return request({
    url: `${v2Host}/user/ad-account/add-assistant`,
    method: 'post',
    data,
  })
}

/**
 * 协助账号列表
 */
export function getAssistantsList(params: {
  id: number
  page_size: number
  page: number
}): _Response<ListData<UserAssistantsInfo>> {
  return request({
    url: `${v2Host}/user/ad-account/list-assistants`,
    method: 'get',
    params,
  })
}

/**
 * 删除协助人
 */
export function deleteAssistant(data: { id: number }): _Response<unknown> {
  return request({
    url: `${v2Host}/user/ad-account/del-assistant`,
    method: 'post',
    data,
  })
}

/**
 * 创意/素材买断
 */
export function billingBuyout(data: {
  basic_id: number
  buy_type: string
  fingerprint: string
  traffic_type: number
}): _Response<unknown> {
  return request({
    url: `${v2Host}/user/billing/buyout`,
    method: 'post',
    data,
  })
}

/**
 * 获取买断/提前支付金额
 */
export function billingPrice(params: {
  basic_id: number
  buy_type: string
  fingerprint: string
  traffic_type: number
}): _Response<{ price: number; amount: number; all: boolean }> {
  return request({
    url: `${v2Host}/user/billing/price`,
    method: 'get',
    params,
  })
}
